import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

export interface UseAdvancedHoverOptions {
  // Time in milliseconds to wait before setting hover state to true
  enterDelay?: number;
  // Time in milliseconds to wait before setting hover state to false
  leaveDelay?: number;
}

/**
 * Tracks hover state of a target element with optional delays for entering and leaving hover state.
 * @param target
 * @param options
 * @returns A tuple containing:
 * - hovering: boolean indicating if the element is currently hovered
 * - willHover: boolean indicating if the element is about to be hovered (before enterDelay)
 * - willUnhover: boolean indicating if the element is about to be unhovered (before leaveDelay)
 */
export function useAdvancedHover(
  target: RefObject<HTMLElement>,
  options: UseAdvancedHoverOptions = {}
): [boolean, boolean, boolean] {
  const { enterDelay, leaveDelay } = options;
  const timeout = useRef<number | undefined>(null);
  const [hovering, setHovering] = useState(false);
  const [willHover, setWillHover] = useState(false);
  const [willUnhover, setWillUnhover] = useState(false);

  const setValue = useCallback((isHovering: boolean) => {
    if (!canHover()) return;

    const delay = isHovering ? enterDelay : leaveDelay;
    window.clearTimeout(timeout.current);

    if (delay) {
      timeout.current = window.setTimeout(() => {
        setHovering(() => isHovering);
        setWillUnhover(() => false);
        setWillHover(() => false);
      }, delay);
      setWillHover(() => isHovering);
      setWillUnhover(() => !isHovering);
    } else {
      setHovering(() => isHovering);
      setWillUnhover(() => false);
      setWillHover(() => false);
    }
  }, []);

  useEffect(() => {
    const node = target?.current;
    if (!node) {
      return;
    }

    const handleMouseOver = () => setValue(true);
    const handleMouseOut = () => setValue(false);

    node.addEventListener('pointerover', handleMouseOver);
    node.addEventListener('pointerout', handleMouseOut);

    return () => {
      node.removeEventListener('pointerover', handleMouseOver);
      node.removeEventListener('pointerout', handleMouseOut);
    };
  }, [target?.current]);

  // Cleans up timeout on unmount
  useEffect(
    () => () => {
      window.clearTimeout(timeout.current);
    },
    []
  );

  return [hovering, willHover, willUnhover];
}

const canHover = (): boolean => (typeof window !== 'undefined' ? !window.matchMedia('(hover: none)').matches : false);
